import { Icon } from '@iconify/react';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Checkbox } from '@/Domain/components/Shared/CheckBox/Checkbox';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { UnifiedInsightItem } from '@/UI/Insights/UnifiedAiInsights/hooks/useUnifiedInsights.hook';

export type InsightVariant = 'flagged' | 'market_insights' | 'alert';

interface InsightListItemProps {
  item: UnifiedInsightItem;
  variant: InsightVariant;
  onTopicClick?: (topic: string) => void;
}

type InsightActionResponse = { ok: boolean; is_no_op: boolean };
type SetReviewedVariables = {
  conv_id: string;
  reviewed: boolean;
  alert_match_id?: number | null;
};

type VariantConfig = {
  icon: string;
  iconColor: string;
  // flagged rows aren't reviewable; the checkbox slot is reserved only when true
  supportsReview: boolean;
  primaryText: (item: UnifiedInsightItem) => string | null;
  insightPresent: (item: UnifiedInsightItem) => boolean;
  reviewedAt: (item: UnifiedInsightItem) => string | null;
};

const VARIANT_CONFIG: Record<InsightVariant, VariantConfig> = {
  flagged: {
    icon: 'mingcute:flag-4-fill',
    iconColor: 'text-red-500',
    supportsReview: false,
    primaryText: (item) => item.flag_reason,
    insightPresent: () => false,
    reviewedAt: () => null,
  },
  market_insights: {
    icon: 'mingcute:bulb-fill',
    iconColor: 'text-amber-500',
    supportsReview: true,
    primaryText: (item) => item.market_insight,
    insightPresent: (item) => item.market_insight !== null,
    reviewedAt: (item) => item.market_insight_reviewed_at,
  },
  alert: {
    icon: 'mingcute:alarm-2-fill',
    iconColor: 'text-orange-500',
    supportsReview: true,
    primaryText: (item) => item.alert_name,
    insightPresent: (item) => item.alert_name !== null,
    reviewedAt: (item) => item.alert_reviewed_at,
  },
};

export const InsightListItem = ({ item, variant, onTopicClick }: InsightListItemProps) => {
  const { scenario } = useParams<{ scenario: string }>();
  const { t } = useTranslation('insights_page', { keyPrefix: 'unified_ai_insights' });
  const { capture } = usePostHogAnalytics();

  const setReviewed = useMutation<InsightActionResponse, Error, SetReviewedVariables>({
    mutationKey: [QueryKey.analytics.setInsightReviewed],
  });

  const config = VARIANT_CONFIG[variant];
  const { icon, iconColor } = config;
  const primaryText = config.primaryText(item);
  const time = format(new Date(item.conversation_timestamp), 'HH:mm');
  // A flag+MI row shown as 'flagged' hides the checkbox even though MI is set;
  // same for alert rows on the All tab when the UCA also has a market_insight.
  const hasReviewableInsight = config.supportsReview && config.insightPresent(item);
  const isReviewed = config.reviewedAt(item) !== null;
  const topicClickable = item.open_ended_topic !== null && onTopicClick !== undefined;

  const handleTopicClick = () => {
    if (item.open_ended_topic && onTopicClick) {
      capture(PostHogEventEnum.InsightsTopicChipClicked, { topic: item.open_ended_topic });
      onTopicClick(item.open_ended_topic);
    }
  };

  const handleToggleReviewed = (next: boolean) => {
    capture(PostHogEventEnum.InsightsSetReviewedToggled, { reviewed: next, variant });
    setReviewed.mutate({
      conv_id: item.conversation_conv_id,
      reviewed: next,
      ...(variant === 'alert' ? { alert_match_id: item.alert_match_id } : {}),
    });
  };

  const handleNavigate = () => {
    if (hasReviewableInsight && !isReviewed) {
      handleToggleReviewed(true);
    }
  };

  return (
    <div
      className="relative flex items-center gap-3 px-4 py-3 rounded-xl border border-border hover:bg-accent transition-colors group"
      data-testid={`insight-item-${variant}`}
    >
      {/* Reserve the checkbox slot on review-capable variants so that during a
          tab switch (when stale rows from the previous filter are still in
          the cache and momentarily render without a checkbox), the row's
          content does not horizontally jitter. */}
      {config.supportsReview && (
        <div className="relative z-10 shrink-0 h-4 w-4 flex items-center justify-center">
          {hasReviewableInsight && (
            <Checkbox
              checked={isReviewed}
              onCheckedChange={(next) => handleToggleReviewed(next === true)}
              aria-label={t('actions.mark_reviewed')}
              title={t('actions.mark_reviewed')}
              className="cursor-pointer"
              data-testid={`insight-reviewed-checkbox-${variant}`}
            />
          )}
        </div>
      )}
      <Link
        to={`/i/${scenario}/${IndexPath.inbox.root}/${item.conversation_conv_id}`}
        onClick={handleNavigate}
        className="absolute inset-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        aria-label={
          variant === 'alert' && primaryText && item.conversation_preview
            ? `${primaryText}: ${item.conversation_preview}`
            : (primaryText ?? item.open_ended_topic ?? undefined)
        }
        data-testid={`insight-item-link-${variant}`}
      />
      <div className={`shrink-0 ${iconColor}`}>
        <Icon icon={icon} className="text-base" />
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground truncate">{primaryText}</p>
          {variant === 'alert' && item.conversation_preview && (
            <p
              className="text-xs text-muted-foreground truncate mt-0.5 italic"
              data-testid="insight-conversation-preview"
            >
              {`“${item.conversation_preview}”`}
            </p>
          )}
        </div>
        {item.open_ended_topic &&
          (topicClickable ? (
            <button
              type="button"
              onClick={handleTopicClick}
              className="relative z-10 shrink-0 cursor-pointer rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              data-testid={`insight-topic-badge-${variant}`}
              aria-label={t('topic_badge_filter_aria', { topic: item.open_ended_topic })}
            >
              <Badge variant="secondary" size="sm" className="hover:bg-accent transition-colors">
                {item.open_ended_topic}
              </Badge>
            </button>
          ) : (
            <Badge
              variant="secondary"
              size="sm"
              className="shrink-0"
              data-testid={`insight-topic-badge-${variant}`}
            >
              {item.open_ended_topic}
            </Badge>
          ))}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-xs text-foreground-subtle">{time}</span>
        <Icon
          icon="mingcute:right-line"
          className="text-sm text-foreground-subtle group-hover:text-muted-foreground transition-colors"
        />
      </div>
    </div>
  );
};
